import { useEffect, useRef, useState } from "react";
import { BOOT_LINES } from "@/lib/bootScript";
import { BootBars, HexDump } from "@/components/BootPanels";
import MyceliumCreep from "@/components/MyceliumCreep";
import useBootSound from "@/hooks/useBootSound";
import { beep } from "@/lib/sfx";
import { sleep } from "@/lib/darknessApi";

const INITIATING_MIN_MS = 3400;
const ESTABLISHED_MS = 2300;

export default function BootSequence({ onDone }) {
  const [count, setCount] = useState(0);
  const [stage, setStage] = useState("log");
  const [glitch, setGlitch] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const mounted = useRef(true);
  const sound = useBootSound(stage, count, BOOT_LINES.length);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (stage !== "log") return;
    if (count >= BOOT_LINES.length) { setStage("initiating"); return; }
    const prev = BOOT_LINES[count - 1];
    const wait = count === 0 ? 500 : prev[2] ?? 130 + Math.random() * 200;
    const t = setTimeout(() => {
      const [type] = BOOT_LINES[count];
      setCount((c) => c + 1);
      if (type === "warn") {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 280);
      }
      try {
        if (type !== "warn") beep(500 + Math.random() * 1100);
        soundRef.current.onLine(type, count);
      } catch (e) {
        /* audio must never block the boot */
      }
    }, wait);
    return () => clearTimeout(t);
  }, [count, stage]);

  useEffect(() => {
    if (stage !== "initiating") return;
    try { soundRef.current.onInitiating(); } catch (e) { /* ignore */ }
    sleep(INITIATING_MIN_MS).then(async () => {
      if (!mounted.current) return;
      setStage("established");
      try { soundRef.current.onEstablished(); } catch (e) { /* ignore */ }
      await sleep(ESTABLISHED_MS);
      if (mounted.current) doneRef.current();
    });
  }, [stage]);

  const lines = BOOT_LINES.slice(Math.max(0, count - 22), count);
  return (
    <div className={`boot ${glitch ? "glitching" : ""} boot-${stage}`} data-testid="boot-sequence" data-boot-stage={stage}>
      <MyceliumCreep />
      <div className="boot-grid">
        <div className="boot-log" data-testid="boot-log">
          {lines.map(([type, text], i) => (
            <p key={count - lines.length + i} className={`boot-line boot-${type}`}>{text}</p>
          ))}
          {stage === "log" && <span className="caret" />}
        </div>
        <aside className="boot-side">
          <BootBars progress={stage === "log" ? count / BOOT_LINES.length : 1} />
          <HexDump running={stage === "log"} />
        </aside>
      </div>
      {stage !== "log" && (
        <div className="boot-banner" data-testid="boot-banner">
          {stage === "initiating" ? (
            <h1 className="boot-initiating glow" data-testid="communication-initiating">COMMUNICATION INITIATING…</h1>
          ) : (
            <h1 className="boot-established glow" data-testid="signal-established">SIGNAL ESTABLISHED</h1>
          )}
        </div>
      )}
    </div>
  );
}
